import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const logoUrl = "https://res.cloudinary.com/dhyp5qe9q/image/upload/v1773774928/episolo/chat-attachments/user-jh73xrq668fpmhwhwhtrjm7vk97z918r-1773774928678.png";

const Navbar = () => {
  const[isOpen, setIsOpen] = useState(false);
  const[isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  },[]);

  const navLinks =[
    { name: "Home", path: "/" },
    {
      name: "Diensten",
      path: "/diensten",
      dropdown:[
        { name: "Alle Diensten", path: "/diensten" },
        { name: "Airco", path: "/diensten/airco" },
        { name: "Warmtepomp", path: "/diensten/warmtepomp" },
        { name: "Zonnepanelen & Elektra", path: "/diensten/zonnepanelen-elektra" },
        { name: "CV & Sanitair", path: "/diensten/cv-sanitair" },
      ],
    },
    { name: "Projecten", path: "/projecten" },
    { name: "Blog", path: "/blog" },
  ];

  return (
    <nav
      className={cn(
        "fixed top-0 w-full z-50 transition-all duration-300",
        isScrolled ? "bg-white shadow-md py-3" : "bg-white/95 backdrop-blur-sm py-5"
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950 shadow-lg ring-1 ring-orange-300/30 p-1.5 group-hover:shadow-xl transition-all duration-300">
              <img
                src={logoUrl}
                alt="SpiroTechniek logo"
                className="h-11 w-11 object-contain"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold tracking-tight text-gray-900 leading-none">
                SPIRO<span className="text-orange-600">TECHNIEK</span>
              </span>
              <span className="text-xs text-gray-500 tracking-wider uppercase">Installatie & Advies</span>
            </div>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) =>
              link.dropdown ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger
                    className={cn(
                      "flex items-center gap-1 text-sm font-medium transition-colors hover:text-orange-600 outline-none",
                      location.pathname.startsWith("/diensten") ? "text-orange-600" : "text-gray-600"
                    )}
                  >
                    {link.name} <ChevronDown className="h-4 w-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {link.dropdown.map((subLink) => (
                      <DropdownMenuItem key={subLink.path} asChild>
                        <Link
                          to={subLink.path}
                          className={cn(
                            "w-full cursor-pointer",
                            location.pathname === subLink.path && "text-orange-600 font-medium"
                          )}
                        >
                          {subLink.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-orange-600",
                    location.pathname === link.path ? "text-orange-600" : "text-gray-600"
                  )}
                >
                  {link.name}
                </Link>
              )
            )}
            <div className="flex items-center gap-4">
              <a
                href="tel:+31658850981"
                className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-orange-600 transition-colors"
              >
                <Phone className="h-4 w-4" />
                06-58850981
              </a>
              <Button
                asChild
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold shadow-lg shadow-orange-500/30"
              >
                <Link to="/offerte">Vraag offerte aan</Link>
              </Button>
            </div>
          </div>

          <button
            className="md:hidden p-2 text-gray-600"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-white border-t border-gray-100 shadow-xl max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col p-4 space-y-2">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div key={link.name} className="flex flex-col">
                  <span className="text-lg font-medium py-2 text-gray-900">
                    {link.name}
                  </span>
                  <div className="flex flex-col pl-4 border-l-2 border-orange-100 ml-2 space-y-2 py-2">
                    {link.dropdown.map((subLink) => (
                      <Link
                        key={subLink.path}
                        to={subLink.path}
                        className={cn(
                          "text-base py-1",
                          location.pathname === subLink.path ? "text-orange-600 font-medium" : "text-gray-600"
                        )}
                        onClick={() => setIsOpen(false)}
                      >
                        {subLink.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    "text-lg font-medium py-2 border-b border-gray-50",
                    location.pathname === link.path ? "text-orange-600" : "text-gray-900"
                  )}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              )
            )}
            <div className="pt-4 pb-2">
              <a
                href="tel:+31658850981"
                className="flex items-center gap-2 text-lg font-semibold text-gray-900 py-2"
              >
                <Phone className="h-5 w-5 text-orange-600" />
                06-58850981
              </a>
            </div>
            <Button
              asChild
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-6"
              onClick={() => setIsOpen(false)}
            >
              <Link to="/offerte">Vraag offerte aan</Link>
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
